import { HistoryMessageItem, IMFile, MessageIncomingEvent, MessageOutgoingEvent, PinnedMessage } from '@wui/im'

import { useLocalization } from '@/shared/composables/localization'
import { isAudioFile, isImageFile, isVideoFile } from '@/shared/utils/files'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { getFullURL } from '@/shared/utils/url'

export type PinnedPreviewIcon = 'fileM' | 'videoM' | 'micM'

export type PinnedPreview = {
  text: string
  icon?: PinnedPreviewIcon
  thumbnailUrl: string | undefined
}

const formatVoiceDuration = (seconds: number) => {
  const total = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(total / 60)
  const rest = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

const getFiles = (
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  pin?: PinnedMessage,
): IMFile[] => {
  const files = message?.item.files ?? pin?.extendedParams?.files ?? []
  return files.filter((file): file is IMFile => Boolean(file?.filename))
}

const isVoiceFile = (file: IMFile) => {
  return file.filemime?.includes('audio/')
    || /^voice-message/i.test(file.filename)
    || isAudioFile(file.filename)
}

const getMessageText = (
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  pin?: PinnedMessage,
) => {
  return sanitizeToText((message?.item.messageText ?? pin?.message ?? '').trim())
}

export const getPinnedPreview = (
  pin: PinnedMessage,
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
): PinnedPreview => {
  const { t, tc } = useLocalization()
  const text = getMessageText(message, pin)
  const files = getFiles(message, pin)
  const images = files.filter((file) => isImageFile(file.filename))
  const audios = files.filter((file) => isVoiceFile(file))
  const videos = files.filter((file) => isVideoFile(file.filename) && !isVoiceFile(file))
  const otherFiles = files.filter((file) => (
    !isImageFile(file.filename) && !isVideoFile(file.filename) && !isVoiceFile(file)
  ))

  const thumbnailUrl = images[0]?.filecookie
    ? getFullURL(`/files/${images[0].filecookie}`)
    : undefined

  if (text && otherFiles.length) {
    return { text, icon: 'fileM', thumbnailUrl }
  }

  if (text && videos.length) {
    return { text, icon: 'videoM', thumbnailUrl }
  }

  if (text) {
    return { text, thumbnailUrl }
  }

  if (images.length && !videos.length && !audios.length && !otherFiles.length) {
    if (images.length === 1) {
      return { text: t('chat.pinnedImage'), thumbnailUrl }
    }

    return {
      text: t('chat.pinnedImagesCount', { count: images.length }),
      thumbnailUrl,
    }
  }

  if (videos.length && !images.length && !audios.length && !otherFiles.length) {
    if (videos.length === 1) {
      return { text: videos[0]?.filename ?? t('common.videos'), icon: 'videoM', thumbnailUrl: undefined }
    }

    return {
      text: t('chat.pinnedVideosCount', { count: videos.length }),
      icon: 'videoM',
      thumbnailUrl: undefined,
    }
  }

  if (audios.length && !images.length && !videos.length && !otherFiles.length) {
    const duration = Number((audios[0] as IMFile & { duration?: number })?.duration ?? 0)
    return {
      text: duration > 0
        ? t('chat.pinnedVoiceMessage', { duration: formatVoiceDuration(duration) })
        : t('chat.pinnedVoiceMessageEmpty'),
      icon: 'micM',
      thumbnailUrl: undefined,
    }
  }

  if (otherFiles.length === 1 && files.length === 1) {
    return { text: otherFiles[0]?.filename ?? '', icon: 'fileM', thumbnailUrl: undefined }
  }

  if (files.length > 1) {
    const config = {
      forOne: tc('common.fileP', 0),
      forTwo: tc('common.fileP', 1),
      forFive: tc('common.fileP', 2),
    }
    return {
      text: getTextForDifferentCount(files.length, config),
      icon: 'fileM',
      thumbnailUrl,
    }
  }

  if (files.length === 1) {
    return {
      text: files[0]?.filename ?? '',
      thumbnailUrl,
    }
  }

  return { text: '', thumbnailUrl }
}
